// =========================================================================
// The PROOF an operator's Ledger gives to vouch for a float's account key
// (audit of 21 Oct 2026, D2; the operator, 22 Oct 2026: "No blind signing"
// — and no message signing anywhere, since the TRON app shows a personal
// message only as a hash).
//
// Account 0's key proves itself: its first address must BE the registered
// device. Accounts 1–3 (Hot, Merchant Hot, Treasury) have no address that is
// the device, so the device vouches — by signing a plain TRX TRANSFER that
// it DISPLAYS in full (amount, to, from) and that the chain can never take:
//
//   from        the registered device (account 0's first address) — the signer;
//   to          the new key's FIRST ADDRESS — what is being vouched for, shown
//               on the device for the operator to compare with the export;
//   amount      0 TRX (a node refuses an amount of zero);
//   expiration  a minute BEFORE its timestamp (a node refuses what has expired);
//   ref block   bytes and hash of no block at all (a node fails TAPOS on it).
//
// Three independent reasons it cannot be broadcast; the timestamp is when it
// was made, and the Worker takes it only for ten minutes. The Worker reads
// the bytes back (parseKeyProof) and recovers the signer from the signature
// over their sha256 — the same signature a real transfer carries.
// =========================================================================

import { sha256 } from '@noble/hashes/sha256';
import { buildTrxTransfer, hexToBytes, bytesToHex, CONTRACT_TYPE_TRANSFER, TYPE_URL_TRANSFER } from './tron-build.mjs';
import { parseStrict, one, many, num, bytes, utf8 } from './protobuf.mjs';
import { encodeTronAddress } from './address.mjs';

/** How long a signed proof stands: a device signature is not a standing credential. */
export const KEY_APPROVAL_WINDOW_MS = 10 * 60_000;
/** The proof expires this long BEFORE it is made. */
export const KEY_PROOF_EXPIRED_BY_MS = 60_000;
const ADDRESS = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;

// Transaction.raw, Transaction.Contract, Any, TransferContract — field numbers.
const RAW = { REF_BLOCK_BYTES: 1, REF_BLOCK_HASH: 4, EXPIRATION: 8, CONTRACT: 11, TIMESTAMP: 14, FEE_LIMIT: 18 };
const CONTRACT = { TYPE: 1, PARAMETER: 2, PERMISSION_ID: 5 };
const ANY = { TYPE_URL: 1, VALUE: 2 };
const TRANSFER = { OWNER: 1, TO: 2, AMOUNT: 3 };

/**
 * @param {{ deviceAddress: string, firstAddress: string, at: number, challenge?: string | null }} i  the registered device (the signer), the new key's first address, when the proof was made, and the desk's challenge (8 bytes of hex) binding it to this registration
 * @returns {{ txID: string, raw_data_hex: string, raw_data: object, visible: false }}
 */
export function buildKeyProof({ deviceAddress, firstAddress, at, challenge = null }) {
  if (typeof deviceAddress !== 'string' || !ADDRESS.test(deviceAddress)) throw new Error('buildKeyProof: deviceAddress must be a TRON address');
  if (typeof firstAddress !== 'string' || !ADDRESS.test(firstAddress)) throw new Error('buildKeyProof: firstAddress must be a TRON address');
  if (!Number.isFinite(at)) throw new Error('buildKeyProof: at must be epoch ms');
  // The desk's challenge (16 hex, 8 bytes) rides where a block hash would: it binds the proof to the
  // operator, the account and the key material the desk minted it for, and the device shows nothing of it.
  // Still on no block: the block bytes stay zero, and no block carries this hash.
  if (challenge !== null && (typeof challenge !== 'string' || !/^[0-9a-f]{16}$/i.test(challenge))) throw new Error('buildKeyProof: challenge must be 8 bytes of hex');
  const timestamp = Math.floor(at);
  const refBlockHash = challenge === null ? new Uint8Array(8) : hexToBytes(challenge.toLowerCase());
  return buildTrxTransfer({
    ownerAddress: deviceAddress, to: firstAddress, amountSun: 0n,
    reference: { refBlockBytes: new Uint8Array(2), refBlockHash }, timestamp, expiration: timestamp - KEY_PROOF_EXPIRED_BY_MS, permissionId: 0,
  });
}

const isZero = (b) => b instanceof Uint8Array && b.every((x) => x === 0);

// Each layer read against its schema: a field the schema does not have — a memo (raw.data), a fee limit, a
// permission, anything appended — is refused, as is a singular field written twice. protobuf-java would skip
// what it does not know and a node would hash it all the same (Screens' Astra review, 23 Oct 2026).
const VARINT = 0, LEN = 2;
const RAW_SCHEMA = {
  [RAW.REF_BLOCK_BYTES]: { name: 'ref_block_bytes', wire: LEN },
  [RAW.REF_BLOCK_HASH]: { name: 'ref_block_hash', wire: LEN },
  [RAW.EXPIRATION]: { name: 'expiration', wire: VARINT },
  [RAW.CONTRACT]: { name: 'contract', wire: LEN, repeated: true },
  [RAW.TIMESTAMP]: { name: 'timestamp', wire: VARINT },
};
const CONTRACT_SCHEMA = { [CONTRACT.TYPE]: { name: 'type', wire: VARINT }, [CONTRACT.PARAMETER]: { name: 'parameter', wire: LEN } };
const ANY_SCHEMA = { [ANY.TYPE_URL]: { name: 'type_url', wire: LEN }, [ANY.VALUE]: { name: 'value', wire: LEN } };
const TRANSFER_SCHEMA = { [TRANSFER.OWNER]: { name: 'owner_address', wire: LEN }, [TRANSFER.TO]: { name: 'to_address', wire: LEN }, [TRANSFER.AMOUNT]: { name: 'amount', wire: VARINT } };

/**
 * The proof read back from its bytes, or null when it is not one of ours:
 * exactly one TransferContract of 0 TRX, expired before it was made, on no
 * block (the block bytes zero; the hash slot carries the desk's challenge),
 * no fee limit, no permission, nothing else in any layer — anything else is
 * not a proof, and a transfer the chain could take is refused here before
 * any signature is looked at.
 * @returns {{ deviceAddress: string, firstAddress: string, at: number, txID: string, challenge: string } | null}
 */
export function parseKeyProof(rawDataHex) {
  let raw;
  try { raw = hexToBytes(rawDataHex); } catch { return null; }
  if (raw.length === 0 || raw.length > 400) return null;
  try {
    const rf = parseStrict(raw, RAW_SCHEMA, 'proof');
    const blockBytes = bytes(rf, RAW.REF_BLOCK_BYTES);
    const blockHash = bytes(rf, RAW.REF_BLOCK_HASH);
    if (!isZero(blockBytes) || blockBytes.length !== 2) return null;
    if (!blockHash || blockHash.length !== 8) return null;
    const expiration = num(rf, RAW.EXPIRATION, -1n);
    const timestamp = num(rf, RAW.TIMESTAMP, -1n);
    if (timestamp <= 0n || expiration < 0n || expiration >= timestamp) return null;
    if (timestamp > BigInt(Number.MAX_SAFE_INTEGER)) return null;
    const contracts = many(rf, RAW.CONTRACT);
    if (contracts.length !== 1) return null;
    const cf = parseStrict(contracts[0], CONTRACT_SCHEMA, 'proof.contract');
    if (num(cf, CONTRACT.TYPE, 0n) !== BigInt(CONTRACT_TYPE_TRANSFER)) return null;
    const anyBuf = one(cf, CONTRACT.PARAMETER);
    if (!anyBuf) return null;
    const af = parseStrict(anyBuf, ANY_SCHEMA, 'proof.parameter');
    if (utf8(af, ANY.TYPE_URL) !== TYPE_URL_TRANSFER) return null;
    const inner = one(af, ANY.VALUE);
    if (!inner) return null;
    const tf = parseStrict(inner, TRANSFER_SCHEMA, 'proof.transfer');
    if (num(tf, TRANSFER.AMOUNT, 0n) !== 0n) return null;
    const owner = bytes(tf, TRANSFER.OWNER);
    const to = bytes(tf, TRANSFER.TO);
    if (!owner || !to || owner.length !== 21 || to.length !== 21 || owner[0] !== 0x41 || to[0] !== 0x41) return null;
    return {
      deviceAddress: encodeTronAddress(owner),
      firstAddress: encodeTronAddress(to),
      at: Number(timestamp),
      txID: bytesToHex(sha256(raw)),
      challenge: bytesToHex(blockHash),
    };
  } catch {
    return null;
  }
}
